import React, { useMemo, useRef } from 'react';
import type { SliderData, SliderItem } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import galleryPlaceholder from 'assets/websiteBuilderDefaults/gallery-placeholder.svg';
import styles from './SliderSection.module.scss';

interface SliderSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): SliderData {
  const items = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[]).map((i) => ({
        imageUrl: typeof i.imageUrl === 'string' ? i.imageUrl : '',
        link: typeof i.link === 'string' ? i.link : '',
      }))
    : [];

  return {
    title: typeof data.title === 'string' ? data.title : '',
    items,
    speed: typeof data.speed === 'number' ? data.speed : 30,
  };
}

export function SliderSectionEditor({
  data,
  onUpdateData,
}: SliderSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIndexRef = useRef<number>(-1);

  const updateItem = (index: number, patch: Partial<SliderItem>) => {
    const next = [...d.items!];
    next[index] = { ...next[index], ...patch };
    onUpdateData({ ...data, items: next });
  };

  const addItem = () => {
    onUpdateData({
      ...data,
      items: [...(d.items ?? []), { imageUrl: '', link: '/' }],
    });
  };

  const removeItem = (index: number) => {
    const next = (d.items ?? []).filter((_, i) => i !== index);
    onUpdateData({ ...data, items: next });
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;
    const index = uploadIndexRef.current;
    compressImageFile(file, { maxWidth: 640, maxHeight: 640, quality: 0.75 })
      .then((imageUrl) => {
        if (index >= 0) {
          updateItem(index, { imageUrl });
        } else {
          onUpdateData({
            ...data,
            items: [...(d.items ?? []), { imageUrl, link: '/' }],
          });
        }
      })
      .catch(() => {});
    e.target.value = '';
    uploadIndexRef.current = -1;
  };

  const triggerImageUpload = (index: number) => {
    uploadIndexRef.current = index;
    fileInputRef.current?.click();
  };

  const triggerAddItemUpload = () => {
    uploadIndexRef.current = -1;
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleImageFileChange}
      />
      <div className={styles.speedControl}>
        <label>Section Title (Optional)</label>
        <input
          type="text"
          className={styles.titleInput}
          value={d.title}
          onChange={(e) => onUpdateData({ ...data, title: e.target.value })}
          placeholder="e.g. Our Trusted Partners"
        />
      </div>

      <div className={styles.grid}>
        {(d.items ?? []).map((item, i) => (
          <div key={i} className={styles.item}>
            <div className={styles.editorItemHeader}>
              <p className={styles.editorItemTitle}>Image {i + 1}</p>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeItem(i)}
              >
                Remove
              </button>
            </div>
            <div className={styles.editorThumbRow}>
              <div className={styles.editorThumb}>
                <img
                  src={
                    item.imageUrl?.trim() ? item.imageUrl : galleryPlaceholder
                  }
                  alt=""
                />
              </div>
              <div className={styles.editorFields}>
                <div className={styles.imageUrlRow}>
                  <input
                    type="text"
                    value={item.imageUrl ?? ''}
                    onChange={(e) =>
                      updateItem(i, { imageUrl: e.target.value })
                    }
                    placeholder="Image URL"
                  />
                  <button
                    type="button"
                    className={styles.browseBtn}
                    onClick={() => triggerImageUpload(i)}
                  >
                    Browse
                  </button>
                </div>
                <input
                  type="text"
                  value={item.link ?? ''}
                  onChange={(e) => updateItem(i, { link: e.target.value })}
                  placeholder="Link URL"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.addRow}>
        <button type="button" className={styles.addBtn} onClick={addItem}>
          + Add item
        </button>
        <button
          type="button"
          className={styles.uploadBtn}
          onClick={triggerAddItemUpload}
        >
          Upload pictures
        </button>
      </div>

      <div className={styles.speedControl}>
        <label>Scroll Speed: {d.speed}s</label>
        <input
          type="range"
          min="5"
          max="120"
          step="5"
          value={d.speed}
          onChange={(e) =>
            onUpdateData({
              ...data,
              speed: Number.parseInt(e.target.value, 10),
            })
          }
        />
        <p style={{ fontSize: '0.65rem', color: '#666', marginTop: '0.2rem' }}>
          Lower is faster, higher is slower.
        </p>
      </div>
    </>
  );
}

interface SliderSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function SliderSection({ data }: SliderSectionProps) {
  const d = getData(data);

  const items = useMemo(() => {
    const list = d.items ?? [];
    if (list.length === 0) {
      return [
        { imageUrl: galleryPlaceholder, link: '/' },
        { imageUrl: galleryPlaceholder, link: '/' },
        { imageUrl: galleryPlaceholder, link: '/' },
        { imageUrl: galleryPlaceholder, link: '/' },
        { imageUrl: galleryPlaceholder, link: '/' },
        { imageUrl: galleryPlaceholder, link: '/' },
      ];
    }
    return list;
  }, [d.items]);

  // Double the items for seamless loop
  const trackItems = [...items, ...items];

  const trackStyle: React.CSSProperties = {
    animationDuration: `${d.speed}s`,
  };

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        {d.title?.trim() && <h2 className={styles.title}>{d.title}</h2>}
        <div className={styles.marqueeContainer}>
          <div className={styles.marqueeTrack} style={trackStyle}>
            {trackItems.map((item, i) => (
              <div key={i} className={styles.marqueeItem}>
                {item.link?.trim() ? (
                  <a href={item.link}>
                    <img
                      src={
                        item.imageUrl?.trim()
                          ? item.imageUrl
                          : galleryPlaceholder
                      }
                      alt=""
                    />
                  </a>
                ) : (
                  <img
                    src={
                      item.imageUrl?.trim() ? item.imageUrl : galleryPlaceholder
                    }
                    alt=""
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default SliderSection;
